import React from 'react';
import { brandAssets } from '../data/mock';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, ArrowRight, ArrowUp } from 'lucide-react';

// X (formerly Twitter) icon component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = ({ hidePreFooterCta = false }) => {
  const currentYear = new Date().getFullYear();

  const coreDomains = [
    { label: "Diagnostics & Insights", href: "/diagnostics" },
    { label: "Coaching & Leadership Development", href: "/coaching" },
    { label: "Capability Building & Training", href: "/capability-building" },
    { label: "Advisory & Organisational Change", href: "/advisory" },
    { label: "Career Selection & Assessment Centres", href: "/early-career-selection" },
    { label: "Leadership & Succession Intelligence", href: "/leadership-succession-intelligence" }
  ];

  const company = [
    { label: "About Us", href: "/about" },
    { label: "How We Work", href: "/engagement" },
    { label: "Impact AS Plus™", href: "/impact-as-plus" },
    { label: "Case Studies & Clients", href: "/case-studies" }
  ];

  const socialLinks = [
    { icon: Linkedin, href: "https://www.linkedin.com/company/resiligent", label: "LinkedIn" },
    { icon: Facebook, href: "https://web.facebook.com/resiligent", label: "Facebook" },
    { icon: Instagram, href: "https://www.instagram.com/resiligent/", label: "Instagram" },
    { icon: XIcon, href: "https://twitter.com/resiligent1", label: "X" }
  ];

  return (
    <footer>
      {/* Pre-footer white spacer section */}
      {!hidePreFooterCta && (
        <div className="bg-white py-16 lg:py-20">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h3 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-2">Ready to Build Leadership That Lasts?</h3>
                <p className="text-slate-600 text-base">Get in touch to discuss how we can support your organisation.</p>
              </div>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3 rounded font-medium text-sm hover:bg-[#1e7ab8] transition-all duration-300"
              >
              Get in Touch
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
      )}

      {/* Main Footer Content - Dark */}
      <div className="bg-[#0a0a0a]">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
            {/* Brand & Description */}
            <div className="lg:col-span-1">
              <a href="/">
                <img 
                  src={brandAssets.logoWhite} 
                  alt="RESILIGENT" 
                  className="h-10 w-auto mb-6"
                />
              </a>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Advisory-led leadership and organisational development for Africa and beyond.
              </p>
              
              {/* Social Media Links */}
              <div className="flex items-center gap-4 mb-6">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#2693d0] hover:text-white transition-all duration-300"
                      aria-label={social.label}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
              
              <a
                href="mailto:info@resiligent.com"
                className="inline-flex items-center gap-2 text-[#2693d0] text-sm font-medium hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                info@resiligent.com
              </a>
            </div>

            {/* Core Domains */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wide mb-6">
                Core Domains
              </h4>
              <nav className="flex flex-col gap-3" aria-label="Core Domains navigation">
                {coreDomains.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-slate-400 text-sm hover:text-[#2693d0] transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wide mb-6">
                Company
              </h4>
              <nav className="flex flex-col gap-3">
                {company.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-slate-400 text-sm hover:text-[#2693d0] transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wide mb-6">
                Contact
              </h4>
              
              {/* West Africa */}
              <div className="mb-6">
                <p className="text-[#2693d0] text-xs font-medium uppercase tracking-wide mb-2">West Africa</p>
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="text-slate-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-400 text-sm leading-relaxed">
                    154 Prince Ade Odedina Street, Victoria Island, Lagos
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-slate-500 w-4 h-4 flex-shrink-0" />
                  <a
                    href="tel:+2347030090712"
                    className="text-slate-400 text-sm hover:text-[#2693d0] transition-colors"
                  >
                    +234 (0)703 009 0712
                  </a>
                </div>
              </div>
              
              {/* Europe */}
              <div>
                <p className="text-[#2693d0] text-xs font-medium uppercase tracking-wide mb-2">Europe</p>
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="text-slate-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Monomark House, 27 Old Gloucester Street, WC1N 3AX, London UK
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-slate-500 w-4 h-4 flex-shrink-0" />
                  <a
                    href="tel:+442071176307"
                    className="text-slate-400 text-sm hover:text-[#2693d0] transition-colors"
                  >
                    +44 (0)207 117 630 7
                  </a>
                </div>
              </div>

              {/* Middle East */}
              <div className="mt-6">
                <p className="text-[#2693d0] text-xs font-medium uppercase tracking-wide mb-2">Middle East</p>
                <div className="flex items-start gap-3">
                  <MapPin className="text-slate-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Dubai, United Arab Emirates
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Navy Blue */}
      <div className="bg-[#1e3a5f]">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-300 text-sm">
              © {currentYear} RESILIGENT. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="/terms-conditions" className="text-slate-300 text-sm hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <a href="/privacy-policy" className="text-slate-300 text-sm hover:text-white transition-colors">
                Privacy Policy
              </a>
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 text-slate-300 text-sm hover:text-white transition-colors"
              >
                Back to Top
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
